import {isStaleWorkerResponseError} from '../../../worker/StaleWorkerResponseError';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core 特征预览执行层；
 * 2. 负责管理交互式预览请求的串行执行与最新请求合并；
 * 3. 不依赖 Vue、DOM 或任何三维对象，确保预览调度策略可被 SDK 独立复用。
 */

/**
 * 预览请求队列执行函数。
 *
 * @template TRequest 预览请求类型。
 * @template TResult 预览结果类型。
 */
export type FeaturePreviewRequestExecutor<TRequest, TResult> = (request: TRequest) => Promise<TResult>;

/**
 * 预览请求队列参数。
 *
 * @template TRequest 预览请求类型。
 * @template TResult 预览结果类型。
 */
export interface FeaturePreviewRequestQueueOptions<TRequest, TResult> {
    /** 预览执行函数。 */
    execute: FeaturePreviewRequestExecutor<TRequest, TResult>;
    /** 非强制请求的合并等待时间，单位毫秒。 */
    delayMs?: number;
    /** 自定义定时器，便于测试或非浏览器宿主注入。 */
    setTimeoutFn?: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
    /** 自定义定时器清理函数，便于测试或非浏览器宿主注入。 */
    clearTimeoutFn?: (handle: ReturnType<typeof setTimeout>) => void;
}

/**
 * 预览请求调度参数。
 */
export interface FeaturePreviewRequestOptions {
    /** 是否跳过延迟立即进入队列。 */
    force?: boolean;
}

/**
 * 预览请求完成结果。
 *
 * @template TResult 预览结果类型。
 */
export type FeaturePreviewRequestQueueResult<TResult> =
    | {
        /** 请求已成功完成。 */
        status: 'completed';
        /** 请求版本号。 */
        token: number;
        /** 预览执行结果。 */
        result: TResult;
    }
    | {
        /** 请求已被更新请求或清理动作判废。 */
        status: 'stale';
        /** 请求版本号。 */
        token: number;
    };

/**
 * 队列内部等待项。
 *
 * @template TRequest 预览请求类型。
 * @template TResult 预览结果类型。
 */
interface PendingPreviewRequest<TRequest, TResult> {
    /** 请求版本号。 */
    token: number;
    /** 预览请求数据。 */
    request: TRequest;
    /** Promise 成功回调。 */
    resolve: (result: FeaturePreviewRequestQueueResult<TResult>) => void;
    /** Promise 失败回调。 */
    reject: (error: unknown) => void;
}

/**
 * 特征预览请求队列。
 *
 * 设计说明：
 * 1. 圆角、倒角这类交互式特征会在选择、输入框变化、参数切换时频繁触发预览；
 * 2. Puppy CAD 内核 求解无法被可靠中断，所以同一 Viewer 内只能允许一个预览请求在途；
 * 3. 当在途请求尚未结束时，新的请求只保留最后一次，旧请求统一标记为 stale，避免 Worker 队列被过期预览塞满。
 *
 * @template TRequest 预览请求类型。
 * @template TResult 预览结果类型。
 */
export class FeaturePreviewRequestQueue<TRequest, TResult> {
    /** 预览执行函数。 */
    private readonly execute: FeaturePreviewRequestExecutor<TRequest, TResult>;
    /** 非强制请求合并等待时间。 */
    private readonly delayMs: number;
    /** 定时器创建函数。 */
    private readonly setTimeoutFn: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
    /** 定时器清理函数。 */
    private readonly clearTimeoutFn: (handle: ReturnType<typeof setTimeout>) => void;
    /** 当前最新请求版本号。 */
    private latestToken = 0;
    /** 正在等待执行的最新请求。 */
    private pendingRequest: PendingPreviewRequest<TRequest, TResult> | null = null;
    /** 当前是否有 Worker 预览正在执行。 */
    private inFlight = false;
    /** 延迟合并定时器句柄。 */
    private timerHandle: ReturnType<typeof setTimeout> | null = null;
    /** 队列是否已经释放。 */
    private disposed = false;

    /**
     * @param options 队列参数。
     */
    public constructor(options: FeaturePreviewRequestQueueOptions<TRequest, TResult>) {
        this.execute = options.execute;
        this.delayMs = options.delayMs ?? 0;
        this.setTimeoutFn = options.setTimeoutFn ?? ((callback, delayMs) => setTimeout(callback, delayMs));
        this.clearTimeoutFn = options.clearTimeoutFn ?? ((handle) => clearTimeout(handle));
    }

    /**
     * 请求一次交互式预览。
     *
     * @param request 预览请求数据。
     * @param options 调度参数。
     * @returns 请求完成状态。被新请求覆盖时返回 stale，不抛出旧请求错误。
     */
    public request(
        request: TRequest,
        options: FeaturePreviewRequestOptions = {},
    ): Promise<FeaturePreviewRequestQueueResult<TResult>> {
        if (this.disposed) {
            return Promise.resolve({
                status: 'stale',
                token: this.latestToken,
            });
        }

        const token = this.nextToken();
        this.resolvePendingAsStale();

        const requestPromise = new Promise<FeaturePreviewRequestQueueResult<TResult>>((resolve, reject) => {
            this.pendingRequest = {
                token,
                request,
                resolve,
                reject,
            };
        });

        if (this.inFlight) {
            this.cancelTimer();
            return requestPromise;
        }

        if (options.force || this.delayMs <= 0) {
            this.cancelTimer();
            this.flushPending();
            return requestPromise;
        }

        this.scheduleFlush();
        return requestPromise;
    }

    /**
     * 清空等待中的请求，并把在途请求判为过期。
     */
    public clear(): void {
        this.nextToken();
        this.cancelTimer();
        this.resolvePendingAsStale();
    }

    /**
     * 释放队列资源。
     */
    public dispose(): void {
        this.disposed = true;
        this.clear();
    }

    /**
     * 递增并返回最新版本号。
     *
     * @returns 最新请求版本号。
     */
    private nextToken(): number {
        this.latestToken += 1;
        return this.latestToken;
    }

    /**
     * 安排延迟执行。
     */
    private scheduleFlush(): void {
        this.cancelTimer();
        this.timerHandle = this.setTimeoutFn(() => {
            this.timerHandle = null;
            this.flushPending();
        }, this.delayMs);
    }

    /**
     * 取消延迟执行。
     */
    private cancelTimer(): void {
        if (this.timerHandle === null) {
            return;
        }
        this.clearTimeoutFn(this.timerHandle);
        this.timerHandle = null;
    }

    /**
     * 把等待中的请求标记为过期。
     */
    private resolvePendingAsStale(): void {
        if (!this.pendingRequest) {
            return;
        }

        const pendingRequest = this.pendingRequest;
        this.pendingRequest = null;
        pendingRequest.resolve({
            status: 'stale',
            token: pendingRequest.token,
        });
    }

    /**
     * 执行当前等待中的请求。
     */
    private flushPending(): void {
        if (this.disposed || this.inFlight || !this.pendingRequest) {
            return;
        }

        const activeRequest = this.pendingRequest;
        this.pendingRequest = null;
        this.inFlight = true;

        void this.execute(activeRequest.request)
            .then((result) => {
                if (
                    this.disposed
                    || activeRequest.token !== this.latestToken
                ) {
                    activeRequest.resolve({
                        status: 'stale',
                        token: activeRequest.token,
                    });
                    return;
                }

                activeRequest.resolve({
                    status: 'completed',
                    token: activeRequest.token,
                    result,
                });
            })
            .catch((error) => {
                /**
                 * 旧请求失败时不能污染最新交互状态。这里把过期失败统一转成 stale，
                 * 只有当前最新请求失败才向上抛出错误，由调用方展示预览失败信息。
                 */
                if (
                    this.disposed
                    || activeRequest.token !== this.latestToken
                    || isStaleWorkerResponseError(error)
                ) {
                    activeRequest.resolve({
                        status: 'stale',
                        token: activeRequest.token,
                    });
                    return;
                }
                activeRequest.reject(error);
            })
            .finally(() => {
                this.inFlight = false;
                if (!this.disposed && this.pendingRequest) {
                    this.flushPending();
                }
            });
    }
}
